import {produce} from 'immer';
import React, {useState} from 'react';

import {
  VisibilityOptionSetting,
  SettingSelectOption,
  VisibilityOptionSettings,
} from 'settings/visibility-options/VisibilityOptions';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import NumericInput from 'ui-library/inputs/NumericInput';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import Modal from 'ui-library/modals/Modal';

function getStateFromSettings(settings: Array<VisibilityOptionSetting>): SettingsState {
  const state: SettingsState = {};
  settings.forEach((s) => {
    state[s.get('name')] = s.get('value');
  });
  return state;
}

function SettingInput({
  setting,
  onUpdateSetting,
  autoFocus,
  settingsState,
}: {
  setting: VisibilityOptionSetting;
  onUpdateSetting: (newSetting: string | boolean | null) => void;
  autoFocus: boolean;
  settingsState: SettingsState;
}): JSX.Element {
  const settingName = setting.get('name');
  const settingValue = settingsState[settingName];
  if (setting.get('display') === 'number-option') {
    return (
      <NumericInput
        onChange={(e) => {
          onUpdateSetting(setting.get('parseStringValue')(e.target.value));
        }}
        // @ts-expect-error: Need to narrow type based on what kind of setting it is
        value={settingValue}
        className="value"
        focusOnMount={autoFocus}
      />
    );
  } else if (setting.get('display') === 'select-option') {
    return (
      <SelectDropdown
        className="value"
        displayName={
          setting
            .get('options')
            .find(
              ({value}: SettingSelectOption) =>
                setting.get('parseStringValue')(value) === setting.get('parseStringValue')(settingValue)
            ).display
        }
        autoFocus={autoFocus}
        menu={
          <SearchableMenu
            width={Menu.WIDTHS.FULL}
            optionDatas={setting.get('options').map(({value, display}: SettingSelectOption) => ({
              value,
              name: display,
              pureTextName: typeof display === 'string' ? display : value,
            }))}
            onSelect={(value: string | boolean | null) => {
              onUpdateSetting(setting.get('parseStringValue')(value));
            }}
            selectedValue={settingValue}
          />
        }
        shouldGrowDisplayName={true}
      />
    );
  } else {
    return (
      <CheckboxInput
        id={settingName}
        label=""
        className="value"
        // @ts-expect-error: Need to narrow type based on what kind of setting it is
        checked={settingValue || false}
        onChange={(e) => {
          onUpdateSetting(e.target.checked);
        }}
      />
    );
  }
}

export interface Props {
  Description?: React.FC;
  settings: VisibilityOptionSettings;
  modalKey?: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

interface SettingsState {
  [settingName: string]: string | boolean | null;
}

function SettingsModal({
  Description,
  settings,
  modalKey,
  onRequestClose,
  onRequestSubmit,
}: Props): JSX.Element {
  const [settingsState, setSettingsState] = useState(getStateFromSettings(settings.models));
  const firstFocusableIndex = settings.models.findIndex(
    (s) => s.get('display') === 'number-option' || s.get('display') === 'select-option'
  );

  return (
    <Modal
      title="Settings"
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'Save', submit: true},
      ]}
      onRequestSubmit={() => {
        settings.models.forEach((setting) => {
          const value = settingsState[setting.get('name')];
          setting.set({value});
        });
        onRequestSubmit();
      }}
      onRequestClose={onRequestClose}
    >
      <div className="visibility-option-settings-modal">
        {settings.models.map((setting, i) => (
          <div key={setting.get('name')} data-name={setting.get('name')} className="row setting">
            <div className="col col-md-6">
              <span>{setting.get('displayName')}</span>
            </div>
            <div className="col col-md-6">
              <SettingInput
                autoFocus={i === firstFocusableIndex}
                setting={setting}
                onUpdateSetting={(newSetting) => {
                  setSettingsState(
                    produce(settingsState, (draft) => {
                      draft[setting.get('name')] = newSetting;
                    })
                  );
                }}
                settingsState={settingsState}
              />
            </div>
          </div>
        ))}
        {Description && (
          <div style={{marginTop: 15}}>
            <Description />
          </div>
        )}
      </div>
    </Modal>
  );
}

export default SettingsModal;
